import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../lib/Seo";
import JourneyCard from "../components/JourneyCard";
import RevealText from "../lib/RevealText";
import { itineraries, categoryFor } from "../data/itineraries";
import { useState, useMemo } from "react";

const countries = ["All", "Kenya", "Tanzania", "Uganda"];

const CATEGORIES = [
  {
    key: "families",
    title: "Itineraries for Families & Travel Enthusiasts",
    subtitle: "4–7 days · Shorter journeys, no less considered",
    eyebrow: "Short-form",
  },
  {
    key: "professionals",
    title: "Itineraries for Wildlife Photographers, Filmmakers and Professionals",
    subtitle: "8 days and beyond · Long-form, in-depth journeys",
    eyebrow: "Long-form",
  },
];

export default function Journeys() {
  const [filter, setFilter] = useState("All");

  const filtered = useMemo(() => {
    if (filter === "All") return itineraries;
    return itineraries.filter((i) => i.country === filter);
  }, [filter]);

  const grouped = useMemo(() => {
    return CATEGORIES.map((c) => ({
      ...c,
      items: filtered.filter((i) => categoryFor(i) === c.key),
    }));
  }, [filtered]);

  return (
    <>
      <Seo
        title="All Journeys | Njia Safaris — Signature African Safaris"
        description="Signature African safaris across Kenya, Tanzania and Uganda — grouped for families & enthusiasts (4–7 days) and for wildlife photographers & professionals (8+ days)."
        path="/journeys"
      />
      <Nav />

      {/* Hero */}
      <section className="pt-40 pb-16 md:pt-56 md:pb-24 container-x" data-testid="journeys-hero">
        <p className="eyebrow mb-6">Journeys · Two paths, {filtered.length} routes</p>
        <RevealText
          as="h1"
          className="h-display text-[14vw] md:text-[9vw] max-w-6xl"
          lines={["Signature", <em key="e" className="italic text-[color:var(--forest)]">journeys.</em>]}
        />
        <div className="grid md:grid-cols-12 gap-8 mt-12">
          <p className="md:col-span-6 md:col-start-7 text-lg leading-relaxed">
            Every journey is a starting point — a proposal, not a package. Choose by the shape of your time:
            <em className="italic font-italic-display text-[color:var(--forest)]"> four to seven days</em> for families and first-time travellers,
            <em className="italic font-italic-display text-[color:var(--forest)]"> eight and beyond</em> for photographers and repeat safari-goers.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="container-x mb-12" data-testid="journeys-filters">
        <div className="hairline" />
        <div className="flex flex-wrap items-center justify-between gap-4 py-6">
          <div className="flex flex-wrap items-center gap-2 md:gap-6">
            {countries.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFilter(c)}
                data-testid={`filter-${c.toLowerCase()}`}
                className={`px-4 py-2 mono text-[11px] uppercase tracking-[0.28em] transition-all duration-500 border ${
                  filter === c
                    ? "bg-[color:var(--ink)] text-[color:var(--ivory)] border-[color:var(--ink)]"
                    : "border-transparent hover:border-[color:var(--line)]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <p className="mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--ink)]/60">
            {filtered.length} {filtered.length === 1 ? "journey" : "journeys"}
          </p>
        </div>
        <div className="hairline" />
      </section>

      {/* Grouped sections */}
      {grouped.map((cat) => (
        <section
          key={cat.key}
          className="container-x pb-24 md:pb-32"
          data-testid={`journeys-category-${cat.key}`}
        >
          {/* Category header */}
          <div className="grid md:grid-cols-12 gap-10 mb-14 md:mb-20 pt-8">
            <div className="md:col-span-4">
              <p className="eyebrow mb-4">{cat.eyebrow}</p>
              <RevealText
                as="h2"
                className="h-display text-3xl md:text-5xl leading-[0.95]"
                lines={[cat.title]}
              />
            </div>
            <div className="md:col-span-6 md:col-start-6 self-end">
              <p className="font-italic-display text-xl md:text-2xl text-[color:var(--forest)] leading-snug max-w-xl">
                {cat.subtitle}
              </p>
              <p className="mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--ink)]/60 mt-4">
                {cat.items.length} {cat.items.length === 1 ? "journey" : "journeys"}
              </p>
            </div>
          </div>

          {cat.items.length === 0 ? (
            <p className="font-italic-display text-2xl text-[color:var(--ink)]/60">
              No journeys in this category for {filter}. Try another country.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-20 md:gap-y-28">
              {cat.items.map((j, i) => (
                <div
                  key={j.slug}
                  className={i % 3 === 1 ? "md:mt-16" : i % 3 === 2 ? "md:mt-28" : ""}
                >
                  <JourneyCard journey={j} index={i} />
                </div>
              ))}
            </div>
          )}
        </section>
      ))}

      <Footer />
    </>
  );
}
